import { useCallback, useEffect, useState } from 'react';
import { FormControlLabel, Switch, Tooltip } from '@mui/material';
import { sessionRole } from '../../authProvider';
import { fetchCaretakerPreference } from './myVehiclesApi';

/**
 * „Tylko moje pojazdy” — przełącznik widoków po opiekunie FTL auta.
 *
 * Status 29.09.2026: opiekun auta po stronie FTL pracuje z listy aut i chce
 * widzieć swoje. Pełne ograniczenie wymaga ról, których klient nie
 * zdefiniował (CLAUDE.md: roli nie zgadujemy), więc to **preferencja, nie
 * uprawnienie** — w duchu wyboru śledzonych działów w powiadomieniach.
 * Użytkownik sam wskazuje w menu użytkownika, którym opiekunem jest
 * (`User.ftlCaretaker`), a przełącznik zawęża widok do aut z tym
 * `Car.ftlCaretaker` — w rekomendacjach pojazdów robi to API (`mine=1`),
 * zanim obetnie listę (02.10.2026). Jeden klik przywraca całość.
 *
 * Zasady:
 * - **pusta preferencja niczego nie ukrywa** — przełącznik jest wtedy
 *   nieaktywny, a zapamiętane „włączone” nie działa;
 * - błąd odczytu preferencji to też brak preferencji (nie pusty widok);
 * - stan przełącznika pamięta przeglądarka, osobno dla każdego widoku;
 *   domyślnie wyłączony — poza planistą (niżej).
 *
 * ## Planista zaczyna od swoich pojazdów (01.10.2026)
 *
 * Podsumowanie statusu z 29.09: „Opiekun pojazdów widzi tylko swoje
 * pojazdy”; omówienie z 01.10: „Samochody widzi swoje pojazdy”. Rola
 * `PLANNER_FTL` ma więc przełącznik domyślnie WŁĄCZONY — o ile wskazała
 * siebie jako opiekuna i nie zapisała własnego wyboru. To **widok domyślny,
 * nie uprawnienie**: jeden klik pokazuje całą flotę, wybór zostaje
 * zapamiętany, serwer niczego w widoku nie ogranicza. Od 07.10.2026 ten
 * sam wybór zawęża planiście WARIANTY do jego aut (odpowiedź Marathonu:
 * „w obszarze zbioru własnych pojazdów”, `userOffers/variantScope.ts`).
 * Inne role — bez zmian.
 */

/** Zdarzenie po zapisie preferencji — otwarte widoki przełączają się bez odświeżania. */
export const CARETAKER_CHANGED_EVENT = 'marathon:ftl-caretaker-changed';

/** Widoki z przełącznikiem — każdy pamięta swój stan. `cars` = lista floty („Samochody”). */
export type MyVehiclesScope = 'fleet-schedule' | 'suggestions' | 'cars';

export const onlyMineStorageKey = (scope: MyVehiclesScope) => `myVehicles.onlyMine.${scope}`;

/** Czy rola zaczyna z włączonym „Tylko moje pojazdy” — widok domyślny, nie uprawnienie. */
export function onlyMineByDefault(role: string | null | undefined): boolean {
  return role === 'PLANNER_FTL';
}

/** Zapamiętany wybór użytkownika; `null` = jeszcze nie wybierał (albo magazyn niedostępny). */
export function readOnlyMineChoice(key: string): boolean | null {
  try {
    const value = localStorage.getItem(key);
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * Zapis wyboru: włączone — `1`. Wyłączone zapisujemy jako `0` tylko wtedy,
 * gdy widok domyślny jest włączony (planista); inaczej brak wpisu znaczy to
 * samo, więc wpis znika — jak przed 01.10.2026.
 */
export function writeOnlyMineChoice(key: string, on: boolean, defaultOn: boolean) {
  try {
    if (on) localStorage.setItem(key, '1');
    else if (defaultOn) localStorage.setItem(key, '0');
    else localStorage.removeItem(key);
  } catch {
    // Magazyn niedostępny (tryb prywatny) — przełącznik działa do odświeżenia.
  }
}

/** Ogłasza nową preferencję otwartym widokom (menu użytkownika → listy). */
export function announceCaretaker(caretaker: string | null) {
  window.dispatchEvent(new CustomEvent(CARETAKER_CHANGED_EVENT, { detail: caretaker }));
}

/**
 * Opiekun FTL wskazany przez użytkownika; `null` = brak (albo błąd odczytu).
 * `enabled: false` — nie pytamy API (np. rola, której preferencja nic nie
 * zmienia, 07.10.2026); wtedy od razu „wczytane”, bez opiekuna.
 */
export function useCaretakerPreference(enabled = true): { caretaker: string | null; loaded: boolean } {
  const [caretaker, setCaretaker] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(!enabled);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchCaretakerPreference()
      .then((p) => {
        if (!cancelled) setCaretaker(p.caretaker);
      })
      .catch(() => {
        if (!cancelled) setCaretaker(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<unknown>).detail;
      setCaretaker(typeof detail === 'string' && detail !== '' ? detail : null);
    };
    window.addEventListener(CARETAKER_CHANGED_EVENT, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(CARETAKER_CHANGED_EVENT, onChange);
    };
  }, [enabled]);

  return { caretaker, loaded };
}

/**
 * Stan przełącznika dla jednego widoku. `onlyMine` jest prawdą tylko przy
 * ustawionej preferencji — bez niej nic nie jest ukrywane, choćby
 * przeglądarka pamiętała „włączone”. Bez zapamiętanego wyboru decyduje widok
 * domyślny roli (`onlyMineByDefault`, rola z tokenu sesji).
 */
export function useOnlyMine(scope: MyVehiclesScope) {
  const key = onlyMineStorageKey(scope);
  const { caretaker, loaded } = useCaretakerPreference();
  const defaultOn = onlyMineByDefault(sessionRole());
  const [stored, setStored] = useState<boolean | null>(() => readOnlyMineChoice(key));
  const setOnlyMine = useCallback(
    (on: boolean) => {
      setStored(on);
      writeOnlyMineChoice(key, on, defaultOn);
    },
    [key, defaultOn],
  );
  return { caretaker, loaded, onlyMine: (stored ?? defaultOn) && caretaker !== null, setOnlyMine };
}

/**
 * Przełącznik „Tylko moje pojazdy”. Bez preferencji wyłączony, z dymkiem,
 * gdzie ją ustawić — żeby nie wyglądał na zepsuty.
 */
export const OnlyMineToggle = ({
  caretaker,
  checked,
  onChange,
}: {
  caretaker: string | null;
  checked: boolean;
  onChange: (on: boolean) => void;
}) => (
  // `describeChild`: dymek opisuje przełącznik, nie zastępuje jego nazwy.
  <Tooltip
    describeChild
    title={
      caretaker === null
        ? 'Aby włączyć, wybierz siebie w polu „Jestem opiekunem FTL” w menu użytkownika (prawy górny róg).'
        : `Pokazuje tylko pojazdy, których opiekunem FTL jest ${caretaker}. To filtr widoku, nie uprawnienie.`
    }
  >
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={checked && caretaker !== null}
          disabled={caretaker === null}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label="Tylko moje pojazdy"
    />
  </Tooltip>
);
